'use strict'

import HSLColor from './HSLColor.js'
import HSVColor from './HSVColor.js'
import LABColor from './LABColor.js'
import LCHColor from './LCHColor.js'
import RGBColor from './RGBColor.js'
import XYZColor from './XYZColor.js'

/**
 * ColorsMath
 * Workflow:
 * - Provide reference white constants and color conversion utilities.
 * - Convert between LAB, XYZ, LCH, sRGB, HSV, and HSL.
 * - Compute DeltaE and helper math used across the visualizer.
 */
class ColorsMath {
    static cxWhite_ref_obs2_ilA = new XYZColor(109.85, 100.0, 35.585)
    static cxWhite_ref_obs2_ilC = new XYZColor(98.074, 100.0, 118.232)
    static cxWhite_ref_obs2_ilD50 = new XYZColor(96.422, 100.0, 82.521)
    static cxWhite_ref_obs2_ilD55 = new XYZColor(95.682, 100.0, 92.149)
    static cxWhite_ref_obs2_ilD65 = new XYZColor(95.047, 100.0, 108.883)
    static cxWhite_ref_obs2_ilD75 = new XYZColor(94.972, 100.0, 122.638)
    static cxWhite_ref_obs2_ilF2 = new XYZColor(99.187, 100.0, 67.395)
    static cxWhite_ref_obs2_ilF7 = new XYZColor(95.044, 100.0, 108.755)
    static cxWhite_ref_obs2_ilF11 = new XYZColor(100.966, 100.0, 64.37)
    static cxWhite_ref_obs10_ilA = new XYZColor(111.144, 100.0, 35.2)
    static cxWhite_ref_obs10_ilC = new XYZColor(97.285, 100.0, 116.145)
    static cxWhite_ref_obs10_ilD50 = new XYZColor(96.72, 100.0, 81.427)
    static cxWhite_ref_obs10_ilD55 = new XYZColor(95.799, 100.0, 90.926)
    static cxWhite_ref_obs10_ilD65 = new XYZColor(94.811, 100.0, 107.304)
    static cxWhite_ref_obs10_ilD75 = new XYZColor(94.416, 100.0, 120.641)
    static cxWhite_ref_obs10_ilF2 = new XYZColor(103.28, 100.0, 69.026)
    static cxWhite_ref_obs10_ilF7 = new XYZColor(95.792, 100.0, 107.687)
    static cxWhite_ref_obs10_ilF11 = new XYZColor(103.866, 100.0, 65.627)
    static CMYK_UNSET_VALUE = -10.0
    static RGB_UNSET_VALUE = -10
    static LAB_UNSET_VALUE = -100000.0

    /**
     * Convert a LAB color to XYZ using a reference white.
     * @param {LABColor} labColor
     * @param {XYZColor} white_ref
     * @returns {XYZColor}
     */
    static cxLABToXYZ(labColor, white_ref) {
        let var_Y = (labColor.l + 16.0) / 116.0
        let var_X = labColor.a / 500.0 + var_Y
        let var_Z = var_Y - labColor.b / 200.0
        if (Math.pow(var_X, 3.0) > 0.008856) {
            var_X = Math.pow(var_X, 3.0)
        } else {
            var_X = (var_X - 0.13793103448275862) / 7.787
        }

        if (Math.pow(var_Y, 3.0) > 0.008856) {
            var_Y = Math.pow(var_Y, 3.0)
        } else {
            var_Y = (var_Y - 0.13793103448275862) / 7.787
        }

        if (Math.pow(var_Z, 3.0) > 0.008856) {
            var_Z = Math.pow(var_Z, 3.0)
        } else {
            var_Z = (var_Z - 0.13793103448275862) / 7.787
        }

        return new XYZColor(white_ref.x * var_X, white_ref.y * var_Y, white_ref.z * var_Z)
    }

    /**
     * Convert an XYZ color to LAB using a reference white.
     * @param {XYZColor} xyzColor
     * @param {XYZColor} white_ref
     * @returns {LABColor}
     */
    static cxXYZToLAB(xyzColor, white_ref) {
        let var_X = xyzColor.x / white_ref.x
        let var_Y = xyzColor.y / white_ref.y
        let var_Z = xyzColor.z / white_ref.z
        if (var_X > 0.008856) {
            var_X = Math.pow(var_X, 1.0 / 3.0)
        } else {
            var_X = 7.787 * var_X + 0.13793103448275862
        }

        if (var_Y > 0.008856) {
            var_Y = Math.pow(var_Y, 1.0 / 3.0)
        } else {
            var_Y = 7.787 * var_Y + 0.13793103448275862
        }

        if (var_Z > 0.008856) {
            var_Z = Math.pow(var_Z, 1.0 / 3.0)
        } else {
            var_Z = 7.787 * var_Z + 0.13793103448275862
        }

        return new LABColor(116.0 * var_Y - 16.0, 500.0 * (var_X - var_Y), 200.0 * (var_Y - var_Z))
    }

    /**
     * Convert sRGB to XYZ (D65).
     * @param {RGBColor} rgbColor
     * @returns {XYZColor}
     */
    static cxSRGBToXYZ_2D65(rgbColor) {
        let var_R = rgbColor.r / 255.0
        let var_G = rgbColor.g / 255.0
        let var_B = rgbColor.b / 255.0
        if (var_R > 0.04045) {
            var_R = Math.pow((var_R + 0.055) / 1.055, 2.4)
        } else {
            var_R /= 12.92
        }

        if (var_G > 0.04045) {
            var_G = Math.pow((var_G + 0.055) / 1.055, 2.4)
        } else {
            var_G /= 12.92
        }

        if (var_B > 0.04045) {
            var_B = Math.pow((var_B + 0.055) / 1.055, 2.4)
        } else {
            var_B /= 12.92
        }

        var_R *= 100.0
        var_G *= 100.0
        var_B *= 100.0
        return new XYZColor(
            var_R * 0.4124564 + var_G * 0.3575761 + var_B * 0.1804375,
            var_R * 0.2126729 + var_G * 0.7151522 + var_B * 0.072175,
            var_R * 0.0193339 + var_G * 0.119192 + var_B * 0.9503041,
        )
    }

    /**
     * Convert XYZ to sRGB (D65).
     * @param {XYZColor} xyzColor
     * @returns {RGBColor}
     */
    static cxXYZToSRGB_2D65(xyzColor) {
        let var_X = xyzColor.x / 100.0
        let var_Y = xyzColor.y / 100.0
        let var_Z = xyzColor.z / 100.0
        let var_R = var_X * 3.2404542 + var_Y * -1.5371385 + var_Z * -0.4985314
        let var_G = var_X * -0.969266 + var_Y * 1.8760108 + var_Z * 0.041556
        let var_B = var_X * 0.0556434 + var_Y * -0.2040259 + var_Z * 1.0572252
        if (var_R > 0.0031308) {
            var_R = 1.055 * Math.pow(var_R, 0.4166666666666667) - 0.055
        } else {
            var_R = 12.92 * var_R
        }

        if (var_G > 0.0031308) {
            var_G = 1.055 * Math.pow(var_G, 0.4166666666666667) - 0.055
        } else {
            var_G = 12.92 * var_G
        }

        if (var_B > 0.0031308) {
            var_B = 1.055 * Math.pow(var_B, 0.4166666666666667) - 0.055
        } else {
            var_B = 12.92 * var_B
        }

        return new RGBColor(
            Math.max(0.0, Math.min(255.0, var_R * 255.0)),
            Math.max(0.0, Math.min(255.0, var_G * 255.0)),
            Math.max(0.0, Math.min(255.0, var_B * 255.0)),
        )
    }

    /**
     * Convert LAB to sRGB (D65).
     * @param {LABColor} labColor
     * @returns {RGBColor}
     */
    static cxLABToSRGB_2D65(labColor) {
        return ColorsMath.cxXYZToSRGB_2D65(ColorsMath.cxLABToXYZ(labColor, ColorsMath.cxWhite_ref_obs2_ilD65))
    }

    /**
     * Convert sRGB to LAB (D65).
     * @param {RGBColor} rgbColor
     * @returns {LABColor}
     */
    static cxSRGBToLAB_2D65(rgbColor) {
        return ColorsMath.cxXYZToLAB(ColorsMath.cxSRGBToXYZ_2D65(rgbColor), ColorsMath.cxWhite_ref_obs2_ilD65)
    }

    /**
     * Convert LAB to LCH.
     * @param {LABColor} labColor
     * @returns {LCHColor}
     */
    static cxLABToLCH(labColor) {
        let var_H = Math.atan2(labColor.b, labColor.a)
        if (var_H > 0.0) {
            var_H = (var_H / Math.PI) * 180.0
        } else {
            var_H = 360.0 - (Math.abs(var_H) / Math.PI) * 180.0
        }

        return new LCHColor(
            labColor.l,
            Math.sqrt(Math.pow(labColor.a, 2.0) + Math.pow(labColor.b, 2.0)),
            var_H,
        )
    }

    /**
     * Convert LCH to LAB.
     * @param {LCHColor} lchColor
     * @returns {LABColor}
     */
    static cxLCHToLAB(lchColor) {
        return new LABColor(
            lchColor.l,
            Math.cos((Math.PI * lchColor.h) / 180.0) * lchColor.c,
            Math.sin((Math.PI * lchColor.h) / 180.0) * lchColor.c,
        )
    }

    /**
     * Convert sRGB to LCH (D65).
     * @param {RGBColor} rgbColor
     * @returns {LCHColor}
     */
    static cxSRGBToLCH_2D65(rgbColor) {
        return ColorsMath.cxLABToLCH(ColorsMath.cxSRGBToLAB_2D65(rgbColor))
    }

    /**
     * Convert LCH to sRGB (D65).
     * @param {LCHColor} lchColor
     * @returns {RGBColor}
     */
    static cxLCHToSRGB_2D65(lchColor) {
        return ColorsMath.cxXYZToSRGB_2D65(ColorsMath.cxLABToXYZ(ColorsMath.cxLCHToLAB(lchColor), ColorsMath.cxWhite_ref_obs2_ilD65))
    }

    /**
     * Convert HSV to RGB.
     * @param {HSVColor} hsvColor
     * @returns {RGBColor}
     */
    static cxHSVToRGB(hsvColor) {
        const rgbColor = new RGBColor()
        let h = hsvColor.h
        if (h > 360.0) {
            h -= 360.0
        }

        if (h < 0.0) {
            h += 360.0
        }

        h = Math.max(0.0, Math.min(360.0, h))
        const s = Math.max(0.0, Math.min(1.0, hsvColor.s))
        const v = Math.max(0.0, Math.min(1.0, hsvColor.v))
        const c = v * s
        const x = c * (1.0 - Math.abs(((h / 60.0) % 2.0) - 1.0))
        const m = v - c
        const a = Math.floor(h / 60.0)
        let rp
        let gp
        let bp
        switch (a) {
            case 0:
                rp = c
                gp = x
                bp = 0.0
                break
            case 1:
                rp = x
                gp = c
                bp = 0.0
                break
            case 2:
                rp = 0.0
                gp = c
                bp = x
                break
            case 3:
                rp = 0.0
                gp = x
                bp = c
                break
            case 4:
                rp = x
                gp = 0.0
                bp = c
                break
            default:
                rp = c
                gp = 0.0
                bp = x
        }

        rgbColor.r = (rp + m) * 255.0
        rgbColor.g = (gp + m) * 255.0
        rgbColor.b = (bp + m) * 255.0
        return rgbColor
    }

    /**
     * Convert RGB to HSV.
     * @param {RGBColor} rgbColor
     * @returns {HSVColor}
     */
    static cxRGBToHSV(rgbColor) {
        const hsvColor = new HSVColor()
        const var_R = rgbColor.r / 255.0
        const var_G = rgbColor.g / 255.0
        const var_B = rgbColor.b / 255.0
        const var_Min = Math.min(var_R, Math.min(var_G, var_B))
        const var_Max = Math.max(var_R, Math.max(var_G, var_B))
        const del_Max = var_Max - var_Min
        hsvColor.v = var_Max
        if (var_Max === 0.0) {
            hsvColor.s = 0.0
        } else {
            hsvColor.s = del_Max / var_Max
        }

        if (del_Max === 0.0) {
            hsvColor.h = 0.0
        } else if (var_Max === var_R) {
            hsvColor.h = 60.0 * (((var_G - var_B) / del_Max) % 6.0)
        } else if (var_Max === var_G) {
            hsvColor.h = 60.0 * ((var_B - var_R) / del_Max + 2.0)
        } else if (var_Max === var_B) {
            hsvColor.h = 60.0 * ((var_R - var_G) / del_Max + 4.0)
        }

        hsvColor.h = (hsvColor.h + 360.0) % 360.0
        return hsvColor
    }

    /**
     * Convert RGB to HSL.
     * @param {RGBColor} rgbColor
     * @returns {HSLColor}
     */
    static cxRGBToHSL(rgbColor) {
        const hslColor = new HSLColor()
        const var_R = rgbColor.r / 255.0
        const var_G = rgbColor.g / 255.0
        const var_B = rgbColor.b / 255.0
        const var_Min = Math.min(Math.min(var_R, var_G), var_B)
        const var_Max = Math.max(Math.max(var_R, var_G), var_B)
        const del_Max = var_Max - var_Min
        hslColor.l = (var_Max + var_Min) / 2.0
        if (del_Max === 0.0) {
            hslColor.h = 0.0
            hslColor.s = 0.0
        } else {
            if (hslColor.l < 0.5) {
                hslColor.s = del_Max / (var_Max + var_Min)
            } else {
                hslColor.s = del_Max / (2.0 - var_Max - var_Min)
            }

            const del_R = ((var_Max - var_R) / 6.0 + del_Max / 2.0) / del_Max
            const del_G = ((var_Max - var_G) / 6.0 + del_Max / 2.0) / del_Max
            const del_B = ((var_Max - var_B) / 6.0 + del_Max / 2.0) / del_Max
            if (var_R === var_Max) {
                hslColor.h = del_B - del_G
            } else if (var_G === var_Max) {
                hslColor.h = 0.3333333333333333 + del_R - del_B
            } else if (var_B === var_Max) {
                hslColor.h = 0.6666666666666666 + del_G - del_R
            }

            if (hslColor.h < 0.0) {
                hslColor.h += 1.0
            }

            if (hslColor.h > 1.0) {
                hslColor.h -= 1.0
            }
        }

        hslColor.h *= 360.0
        return hslColor
    }

    /**
     * Convert HSL to RGB.
     * @param {HSLColor} hslColor
     * @returns {RGBColor}
     */
    static cxHSLToRGB(hslColor) {
        hslColor.h /= 360.0
        const rgbColor = new RGBColor()
        if (hslColor.s === 0.0) {
            rgbColor.r = hslColor.l * 255.0
            rgbColor.g = hslColor.l * 255.0
            rgbColor.b = hslColor.l * 255.0
        } else {
            let var_2
            if (hslColor.l < 0.5) {
                var_2 = hslColor.l * (1.0 + hslColor.s)
            } else {
                var_2 = hslColor.l + hslColor.s - hslColor.s * hslColor.l
            }

            const var_1 = 2.0 * hslColor.l - var_2
            rgbColor.r = 255.0 * ColorsMath.Hue_2_RGB(var_1, var_2, hslColor.h + 0.3333333333333333)
            rgbColor.g = 255.0 * ColorsMath.Hue_2_RGB(var_1, var_2, hslColor.h)
            rgbColor.b = 255.0 * ColorsMath.Hue_2_RGB(var_1, var_2, hslColor.h - 0.3333333333333333)
        }

        return rgbColor
    }

    /**
     * Convert HSL to HSV.
     * @param {HSLColor} hslColor
     * @returns {HSVColor}
     */
    static cxHSLToHSV(hslColor) {
        const hsvColor = new HSVColor()
        hsvColor.h = hslColor.h
        const ll = hslColor.l * 2.0
        const ss = hslColor.s * (ll <= 1.0 ? ll : 2.0 - ll)
        hsvColor.v = (ll + ss) / 2.0
        hsvColor.s = (2.0 * ss) / (ll + ss)
        return hsvColor
    }

    /**
     * Compute RGB DeltaE (Euclidean in RGB space).
     * @param {RGBColor} color1
     * @param {RGBColor} color2
     * @returns {number}
     */
    static cxDeltaErgb(color1, color2) {
        return Math.sqrt(
            Math.pow(color1.r - color2.r, 2.0) + Math.pow(color1.g - color2.g, 2.0) + Math.pow(color1.b - color2.b, 2.0),
        )
    }

    /**
     * Compute DeltaE CIE 1976.
     * @param {LABColor} color1
     * @param {LABColor} color2
     * @returns {number}
     */
    static cxDeltaECIE1976(color1, color2) {
        return Math.sqrt(
            Math.pow(color1.l - color2.l, 2.0) +
                Math.pow(color1.a - color2.a, 2.0) +
                Math.pow(color1.b - color2.b, 2.0),
        )
    }

    /**
     * Compute DeltaE CIE 1994 with custom parameters.
     * @param {LABColor} color1
     * @param {LABColor} color2
     * @param {number} k1
     * @param {number} k2
     * @param {number} kL
     * @param {number} kC
     * @param {number} kH
     * @returns {number}
     */
    static cxDeltaECIE1994(color1, color2, k1, k2, kL, kC, kH) {
        const xC1 = Math.sqrt(Math.pow(color1.a, 2.0) + Math.pow(color1.b, 2.0))
        const xC2 = Math.sqrt(Math.pow(color2.a, 2.0) + Math.pow(color2.b, 2.0))
        const deltaL = color1.l - color2.l
        const deltaC = xC1 - xC2
        const deltaA = color1.a - color2.a
        const deltaB = color1.b - color2.b
        const deltaH = Math.sqrt(deltaA * deltaA + deltaB * deltaB - deltaC * deltaC)
        const sC = 1.0 + k1 * xC1
        const sH = 1.0 + k2 * xC1
        return Math.sqrt(
            Math.pow(deltaL / kL, 2.0) +
                Math.pow(deltaC / (kC * sC), 2.0) +
                Math.pow(deltaH / (kH * sH), 2.0),
        )
    }

    /**
     * Compute DeltaE CIE 1994 with default parameters.
     * @param {LABColor} color1
     * @param {LABColor} color2
     * @returns {number}
     */
    static cxDeltaECIE1994Default(color1, color2) {
        return ColorsMath.cxDeltaECIE1994(color1, color2, 0.045, 0.015, 1.0, 1.0, 1.0)
    }

    /**
     * Compute DeltaE CIE 1994 for graphic arts.
     * @param {LABColor} color1
     * @param {LABColor} color2
     * @returns {number}
     */
    static cxDeltaECIE1994GraphicArts(color1, color2) {
        return ColorsMath.cxDeltaECIE1994(color1, color2, 0.045, 0.015, 1.0, 1.0, 1.0)
    }

    /**
     * Compute DeltaE CIE 1994 for textiles.
     * @param {LABColor} color1
     * @param {LABColor} color2
     * @returns {number}
     */
    static cxDeltaECIE1994Textiles(color1, color2) {
        return ColorsMath.cxDeltaECIE1994(color1, color2, 0.048, 0.014, 2.0, 1.0, 1.0)
    }

    /**
     * Compute DeltaE CIE 2000 with custom parameters.
     * @param {LABColor} color1
     * @param {LABColor} color2
     * @param {number} kL
     * @param {number} kC
     * @param {number} kH
     * @returns {number}
     */
    static cxDeltaECIE2000(color1, color2, kL, kC, kH) {
        const xLBis = (color1.l + color2.l) / 2.0
        const c1 = Math.sqrt(color1.a * color1.a + color1.b * color1.b)
        const c2 = Math.sqrt(color2.a * color2.a + color2.b * color2.b)
        const xC = (c1 + c2) / 2.0
        const g =
            (1.0 - Math.sqrt(Math.pow(xC, 7.0) / (Math.pow(xC, 7.0) + Math.pow(25.0, 7.0)))) / 2.0
        const a1Bis = color1.a * (1.0 + g)
        const a2Bis = color2.a * (1.0 + g)
        const c1Bis = Math.sqrt(a1Bis * a1Bis + color1.b * color1.b)
        const c2Bis = Math.sqrt(a2Bis * a2Bis + color2.b * color2.b)
        const xCBis = (c1Bis + c2Bis) / 2.0
        let h1Bis = ColorsMath.rad2deg(Math.atan2(color1.b, a1Bis))
        if (h1Bis < 0.0) {
            h1Bis += 360.0
        }

        let h2Bis = ColorsMath.rad2deg(Math.atan2(color2.b, a2Bis))
        if (h2Bis < 0.0) {
            h2Bis += 360.0
        }

        let xHBis = (h1Bis + h2Bis) / 2.0
        if (Math.abs(h1Bis - h2Bis) > 180.0) {
            xHBis += 180.0
        }

        const t =
            1.0 -
            0.17 * Math.cos(ColorsMath.deg2rad(xHBis - 30.0)) +
            0.24 * Math.cos(ColorsMath.deg2rad(2.0 * xHBis)) +
            0.32 * Math.cos(ColorsMath.deg2rad(3.0 * xHBis + 6.0)) -
            0.2 * Math.cos(ColorsMath.deg2rad(4.0 * xHBis - 63.0))
        let deltahBis
        if (Math.abs(h2Bis - h1Bis) <= 180.0) {
            deltahBis = h2Bis - h1Bis
        } else if (Math.abs(h2Bis - h1Bis) > 180.0 && h2Bis <= h1Bis) {
            deltahBis = h2Bis - h1Bis + 360.0
        } else {
            deltahBis = h2Bis - h1Bis - 360.0
        }

        const deltaLBis = color2.l - color1.l
        const deltaCBis = c2Bis - c1Bis
        const deltaHBis = 2.0 * Math.sqrt(c1Bis * c2Bis) * Math.sin(ColorsMath.deg2rad(deltahBis / 2.0))
        const sL = 1.0 + (0.015 * Math.pow(xLBis - 50.0, 2.0)) / Math.sqrt(20.0 + Math.pow(xLBis - 50.0, 2.0))
        const sC = 1.0 + 0.045 * xCBis
        const sH = 1.0 + 0.015 * xCBis * t
        const deltaO = 30.0 * Math.exp(-Math.pow((xHBis - 275.0) / 25.0, 2.0))
        const rC = 2.0 * Math.sqrt(Math.pow(xCBis, 7.0) / (Math.pow(xCBis, 7.0) + Math.pow(25.0, 7.0)))
        const rT = -rC * Math.sin(ColorsMath.deg2rad(2.0 * deltaO))
        return Math.sqrt(
            Math.pow(deltaLBis / (kL * sL), 2.0) +
                Math.pow(deltaCBis / (kC * sC), 2.0) +
                Math.pow(deltaHBis / (kH * sH), 2.0) +
                rT * (deltaCBis / (kC * sC)) * (deltaHBis / (kH * sH)),
        )
    }

    /**
     * Compute DeltaE CIE 2000 with default parameters.
     * @param {LABColor} color1
     * @param {LABColor} color2
     * @returns {number}
     */
    static cxDeltaECIE2000Default(color1, color2) {
        return ColorsMath.cxDeltaECIE2000(color1, color2, 1.0, 1.0, 1.0)
    }

    /**
     * Compute DeltaE CMC with custom parameters.
     * @param {LABColor} color1
     * @param {LABColor} color2
     * @param {number} l
     * @param {number} c
     * @returns {number}
     */
    static cxDeltaECMC(color1, color2, l, c) {
        const c1 = Math.sqrt(color1.a * color1.a + color1.b * color1.b)
        const c2 = Math.sqrt(color2.a * color2.a + color2.b * color2.b)
        const deltaC = c1 - c2
        const deltaL = color1.l - color2.l
        const deltaA = color1.a - color2.a
        const deltaB = color1.b - color2.b
        const deltaH = Math.sqrt(deltaA * deltaA + deltaB * deltaB - deltaC * deltaC)
        let sL = 0.511
        if (color1.l >= 16.0) {
            sL = (0.040975 * color1.l) / (1.0 + 0.01765 * color1.l)
        }

        const sC = (0.0638 * c1) / (1.0 + 0.0131 * c1) + 0.638
        const f = Math.sqrt(Math.pow(c1, 4.0) / (Math.pow(c1, 4.0) + 1900.0))
        const h1 = ColorsMath.rad2deg(Math.atan2(color1.b, color1.a))
        let t = 0.0
        if (164.0 <= h1 && h1 <= 345.0) {
            t = 0.56 + Math.abs(0.2 * Math.cos(ColorsMath.deg2rad(h1 + 168.0)))
        } else {
            t = 0.36 + Math.abs(0.4 * Math.cos(ColorsMath.deg2rad(h1 + 35.0)))
        }

        const sH = sC * (f * t + 1.0 - f)
        return Math.sqrt(
            Math.pow(deltaL / (l * sL), 2.0) + Math.pow(deltaC / (c * sC), 2.0) + Math.pow(deltaH / sH, 2.0),
        )
    }

    /**
     * Compute DeltaE CMC (1:1).
     * @param {LABColor} color1
     * @param {LABColor} color2
     * @returns {number}
     */
    static cxDeltaECMC1_1(color1, color2) {
        return ColorsMath.cxDeltaECMC(color1, color2, 1.0, 1.0)
    }

    /**
     * Compute DeltaE CMC (2:1).
     * @param {LABColor} color1
     * @param {LABColor} color2
     * @returns {number}
     */
    static cxDeltaECMC2_1(color1, color2) {
        return ColorsMath.cxDeltaECMC(color1, color2, 2.0, 1.0)
    }

    /**
     * Apply gain to a channel with a black level.
     * @param {number} colorValue
     * @param {number} gain
     * @param {number} blackLevel
     * @returns {number}
     */
    static cxGainedColorValue(colorValue, gain, blackLevel) {
        return blackLevel + (colorValue - blackLevel) * gain
    }

    /**
     * Reverse gain applied to a channel with a black level.
     * @param {number} gainedcolorValue
     * @param {number} gain
     * @param {number} blackLevel
     * @returns {number}
     */
    static cxInvertedGainColorValue(gainedcolorValue, gain, blackLevel) {
        return blackLevel + (gainedcolorValue - blackLevel) / gain
    }

    /**
     * Helper for HSL -> RGB conversion (Hue to RGB component).
     * @param {number} v1
     * @param {number} v2
     * @param {number} vH
     * @returns {number}
     */
    static Hue_2_RGB(v1, v2, vH) {
        let value = vH
        if (value < 0.0) {
            value += 1.0
        }

        if (value > 1.0) {
            value -= 1.0
        }

        if (6.0 * value < 1.0) {
            return v1 + (v2 - v1) * 6.0 * value
        } else if (2.0 * value < 1.0) {
            return v2
        }

        return 3.0 * value < 2.0 ? v1 + (v2 - v1) * (0.6666666666666666 - value) * 6.0 : v1
    }

    /**
     * Convert LAB a/b to a hue angle.
     * @param {number} a
     * @param {number} b
     * @returns {number}
     */
    static cieLab_2_Hue(a, b) {
        let var_bias = 0.0
        if (a >= 0.0 && b === 0.0) {
            return 0.0
        } else if (a < 0.0 && b === 0.0) {
            return 180.0
        } else if (a === 0.0 && b > 0.0) {
            return 90.0
        } else if (a === 0.0 && b < 0.0) {
            return 270.0
        }

        if (a > 0.0 && b > 0.0) {
            var_bias = 0.0
        }

        if (a < 0.0) {
            var_bias = 180.0
        }

        if (a > 0.0 && b < 0.0) {
            var_bias = 360.0
        }

        return ColorsMath.rad2deg(Math.atan(b / a)) + var_bias
    }

    /**
     * Degrees to radians.
     * @param {number} deg
     * @returns {number}
     */
    static deg2rad(deg) {
        return (deg * Math.PI) / 180.0
    }

    /**
     * Radians to degrees.
     * @param {number} rad
     * @returns {number}
     */
    static rad2deg(rad) {
        return (rad * 180.0) / Math.PI
    }

    /**
     * Compute LRV value from LAB.
     * @param {LABColor} color
     * @returns {number}
     */
    static cxLABToLRVValue(color) {
        return ColorsMath.cxWhite_ref_obs10_ilD65.y * Math.pow((color.l + 16.0) / 116.0, 3.0)
    }
}

export default ColorsMath
